import { normalize } from 'normalizr'
import { Schemas } from '@redux/schema'
import { deleteEntity, updateEntities } from '@redux/entities'

import api from '@functions/api'

import type { LayoutTemplateModel } from '@models/layoutTemplate'
import type { AppDispatch } from '@redux/store'
import type {
  Action, DeleteParams, EntityOptions, Errors, FetchRequestPayload, RootReducerState, UpdateParams,
} from '@redux/modules/types'

import { handleError } from '../utils'

const FETCH_REQUEST = 'realbase/layoutTemplate/FETCH_REQUEST'
const FETCH_SUCCESS = 'realbase/layoutTemplate/FETCH_SUCCESS'
const FETCH_FAILURE = 'realbase/layoutTemplate/FETCH_FAILURE'

const CREATE_REQUEST = 'realbase/layoutTemplate/CREATE_REQUEST'
const CREATE_SUCCESS = 'realbase/layoutTemplate/CREATE_SUCCESS'
const CREATE_FAILURE = 'realbase/layoutTemplate/CREATE_FAILURE'

const UPDATE_REQUEST = 'realbase/layoutTemplate/UPDATE_REQUEST'
const UPDATE_SUCCESS = 'realbase/layoutTemplate/UPDATE_SUCCESS'
const UPDATE_FAILURE = 'realbase/layoutTemplate/UPDATE_FAILURE'

const DELETE_REQUEST = 'realbase/layoutTemplate/DELETE_REQUEST'
const DELETE_SUCCESS = 'realbase/layoutTemplate/DELETE_SUCCESS'
const DELETE_FAILURE = 'realbase/layoutTemplate/DELETE_FAILURE'

export type LayoutTemplateModuleState = {
  creating: boolean,
  deleting: boolean,
  errors: string[],
  loaded: boolean,
  loadedForKeys: string[],
  loadedIds: number[],
  loading: boolean,
  updating: boolean,
}

// Initial State
const initialState: LayoutTemplateModuleState = {
  creating: false,
  deleting: false,
  errors: [],
  loaded: false,
  loadedIds: [],
  loadedForKeys: [],
  loading: false,
  updating: false,
}

// Actions
export function fetchRequest(payload: FetchRequestPayload = {}) {
  return {
    type: FETCH_REQUEST,
    loadedForKeys: payload.loadedForKeys,
    loadedIds: payload.loadedIds,
  }
}

export function fetchSuccess() {
  return {
    type: FETCH_SUCCESS,
  }
}

export function fetchFailure(errors: Errors = []) {
  return {
    type: FETCH_FAILURE,
    errors,
  }
}

export function createRequest() {
  return {
    type: CREATE_REQUEST,
  }
}

export function createSuccess() {
  return {
    type: CREATE_SUCCESS,
  }
}

export function createFailure(errors: Errors = []) {
  return {
    type: CREATE_FAILURE,
    errors,
  }
}

export function updateRequest() {
  return {
    type: UPDATE_REQUEST,
  }
}

export function updateSuccess() {
  return {
    type: UPDATE_SUCCESS,
  }
}

export function updateFailure(errors: Errors = []) {
  return {
    type: UPDATE_FAILURE,
    errors,
  }
}

export function deleteRequest() {
  return {
    type: DELETE_REQUEST,
  }
}

export function deleteSuccess() {
  return {
    type: DELETE_SUCCESS,
  }
}

export function deleteFailure(errors: Errors = []) {
  return {
    type: DELETE_FAILURE,
    errors,
  }
}

// Action Creators
export function loadLayoutTemplates(options: EntityOptions = {}) {
  const { entityKey } = options

  return (dispatch: AppDispatch, getState: () => RootReducerState) => {
    const loadedForKeys = [...getState().layoutTemplates.loadedForKeys]

    if (entityKey && !loadedForKeys.includes(entityKey)){
      loadedForKeys.push(entityKey)
    }

    dispatch(fetchRequest({ loadedForKeys }))

    const promise = api('/layout_templates.json', options)
      .then(({ data }: { data: LayoutTemplateModel[] }) => {
        const normalizedJson = normalize(data, Schemas.LAYOUT_TEMPLATE_ARRAY)
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function loadLayoutTemplate(layoutTemplateId: number, options: EntityOptions = {}) {
  const { entityKey } = options

  return (dispatch: AppDispatch, getState: () => RootReducerState) => {
    const state = getState()
    const loadedIds = [...state.layoutTemplates.loadedIds]
    const loadedForKeys = [...state.layoutTemplates.loadedForKeys]

    if (layoutTemplateId && !loadedIds.includes(layoutTemplateId)){
      loadedIds.push(layoutTemplateId)
    }

    if (entityKey && !loadedForKeys.includes(entityKey)){
      loadedForKeys.push(entityKey)
    }

    // Set Loading
    dispatch(fetchRequest({ loadedForKeys, loadedIds }))

    const promise = api(`/layout_templates/${layoutTemplateId}.json`, options)
      .then(({ data }: { data: LayoutTemplateModel }) => {
        const normalizedJson = normalize(data, Schemas.LAYOUT_TEMPLATE)
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function createLayoutTemplate(layoutTemplate: Partial<LayoutTemplateModel>, options: EntityOptions = {}) {
  const config = {
    method: 'POST',
    data: JSON.stringify({
      layout_template: layoutTemplate,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(createRequest())

    const promise = api('/layout_templates.json', options, config)
      .then(({ data }: { data: LayoutTemplateModel }) => {
        const normalizedJson = normalize(data, Schemas.LAYOUT_TEMPLATE)
        dispatch(updateEntities(normalizedJson))
        dispatch(createSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(createFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function updateLayoutTemplate(
  layoutTemplate: UpdateParams<LayoutTemplateModel>,
  options: EntityOptions = {},
) {
  const config = {
    method: 'PUT',
    data: JSON.stringify({
      layout_template: layoutTemplate,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(updateRequest())

    const promise = api(`/layout_templates/${layoutTemplate.id}.json`, options, config)
      .then(({ data }: { data: LayoutTemplateModel }) => {
        const normalizedJson = normalize(data, Schemas.LAYOUT_TEMPLATE)
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(updateFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function duplicateLayoutTemplate(layoutTemplate: Partial<LayoutTemplateModel>, options: EntityOptions = {}) {
  const { id } = layoutTemplate

  const config = {
    method: 'POST',
  }

  return (dispatch: AppDispatch) => {
    dispatch(createRequest())

    const promise = api(`/layout_templates/${id}/duplicate.json`, options, config)
      .then(({ data }: { data: LayoutTemplateModel }) => {
        // Add New Template
        const normalizedJson = normalize(data, Schemas.LAYOUT_TEMPLATE)
        dispatch(updateEntities(normalizedJson))

        dispatch(createSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(createFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function toggleLayoutTemplateVisibility(layoutTemplate: Partial<LayoutTemplateModel>, options: EntityOptions = {}) {
  const { id } = layoutTemplate

  const config = {
    method: 'PUT',
  }

  return (dispatch: AppDispatch) => {
    dispatch(createRequest())

    const promise = api(`/layout_templates/${id}/toggle_layout_template_visibility.json`, options, config)
      .then(({ data }: { data: LayoutTemplateModel }) => {
        // Add New Template
        const normalizedJson = normalize(data, Schemas.LAYOUT_TEMPLATE)
        dispatch(updateEntities(normalizedJson))

        dispatch(createSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(createFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function deleteLayoutTemplate(layoutTemplate: DeleteParams<LayoutTemplateModel>) {
  const config = {
    method: 'DELETE',
  }

  return (dispatch: AppDispatch) => {
    dispatch(deleteRequest())

    const promise = api(`/layout_templates/${layoutTemplate.id}.json`, {}, config)
      .then(({ data }: { data: { id: number } }) => {
        const normalizedJson = normalize(data, Schemas.LAYOUT_TEMPLATE)
        dispatch(deleteEntity(normalizedJson))
        dispatch(deleteSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(deleteFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function updateCompatibleProducts(
  layoutTemplate: UpdateParams<LayoutTemplateModel>,
  publicationProduct: number[],
  options: EntityOptions = {},
) {
  const config = {
    method: 'PUT',
    data: JSON.stringify({
      publication_product: publicationProduct,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(updateRequest())

    const promise = api(`/layout_templates/${layoutTemplate.id}/update_products.json`, options, config)
      .then(({ data }: { data: LayoutTemplateModel }) => {
        const normalizedJson = normalize(data, Schemas.LAYOUT_TEMPLATE)
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(updateFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

// Reducer
export default function reducer(
  state: LayoutTemplateModuleState = initialState,
  action: Action = { type: '' },
): LayoutTemplateModuleState {
  switch (action.type){
    case FETCH_REQUEST:
      return {
        ...state,
        loadedForKeys: action.loadedForKeys || state.loadedForKeys,
        loadedIds: action.loadedIds || state.loadedIds,
        loading: true,
      }
    case FETCH_SUCCESS:
      return {
        ...state,
        loading: false,
        loaded: true,
        errors: [],
      }
    case FETCH_FAILURE:
      return {
        ...state,
        loading: false,
        loaded: false,
        errors: action.errors || [],
      }
    case CREATE_REQUEST:
      return {
        ...state,
        creating: true,
      }
    case CREATE_SUCCESS:
      return {
        ...state,
        creating: false,
        errors: [],
      }
    case CREATE_FAILURE:
      return {
        ...state,
        creating: false,
        errors: action.errors || [],
      }
    case UPDATE_REQUEST:
      return {
        ...state,
        updating: true,
      }
    case UPDATE_SUCCESS:
      return {
        ...state,
        updating: false,
        errors: [],
      }
    case UPDATE_FAILURE:
      return {
        ...state,
        updating: false,
        errors: action.errors || [],
      }
    case DELETE_REQUEST:
      return {
        ...state,
        deleting: true,
      }
    case DELETE_SUCCESS:
      return {
        ...state,
        deleting: false,
        errors: [],
      }
    case DELETE_FAILURE:
      return {
        ...state,
        deleting: false,
        errors: action.errors || [],
      }
    default:
      return state
  }
}
